import React from 'react';
import { VIDEO_INTENTS } from '../hooks/useYoutubeSearch';
import './VideoSlots.css';

/**
 * The three saved videos for a song — the "ready to go" row.
 *
 * ONE CHIP PER KIND, ALWAYS ALL THREE ON SCREEN. A filled chip plays straight
 * into the popout; an empty one opens the picker on that kind. Empty chips are
 * muted with a colour token and never hidden: the studio's primary employee-
 * portal user has keratoconus, and the installed PWA has no hover at all, so a
 * control that appears only on hover or only once filled is the hardest
 * possible thing to find.
 *
 * The row is also the answer to "which of these does this song have?" without
 * clicking anything, which is the question a teacher actually has thirty
 * seconds before a lesson.
 *
 * MAKE A TRACK (2026-09-27): under the chips, one button per SAVED video sends
 * its link to the Add a track queue (useVideoTrack). Its state is shown on
 * the button itself, from the queue, so a second tap cannot make the same
 * track twice. Absent entirely without a student and song (no onMakeTrack).
 */
const TRACK_BUTTON = {
	idle: { icon: 'fa-waveform-lines', text: (label) => `Track from ${label}` },
	working: { icon: 'fa-spinner-third fa-spin', text: (label) => `Making ${label} track…` },
	done: { icon: 'fa-circle-check', text: (label) => `${label} track added` },
	failed: { icon: 'fa-rotate-right', text: (label) => `${label} track failed: try again` },
};

const VideoSlots = ({ videos = {}, onPlay, onEdit, disabled = false, trackStateFor = null, onMakeTrack = null }) => (
	<>
	<div className="ld-vslots" role="group" aria-label="Saved videos">
		{VIDEO_INTENTS.map(({ key, label }) => {
			const filled = Boolean(videos[key]);
			return (
				<button
					key={key}
					type="button"
					className={`ld-vslots__chip${filled ? ' is-filled' : ''}`}
					disabled={disabled}
					aria-label={filled ? `Play ${label} video` : `Add a ${label} video`}
					onClick={() => (filled ? onPlay(key) : onEdit(key))}>
					<i
						className={`fa-sharp fa-solid ${filled ? 'fa-play' : 'fa-plus'}`}
						aria-hidden="true"
					/>
					{label}
					{/* Change is its own control rather than a long-press or a
					    right-click: both are invisible affordances, and this one
					    has to be reachable by keyboard anyway. Rendered as a
					    sibling, not nested — a button inside a button is invalid
					    HTML and React will not deliver the inner click. */}
				</button>
			);
		})}
		{VIDEO_INTENTS.some(({ key }) => videos[key]) && (
			<button
				type="button"
				className="ld-vslots__manage"
				disabled={disabled}
				onClick={() => onEdit(VIDEO_INTENTS.find(({ key }) => videos[key]).key)}>
				Change
			</button>
		)}
	</div>
	{onMakeTrack && trackStateFor && VIDEO_INTENTS.some(({ key }) => videos[key]) && (
		<div className="ld-vslots ld-vslots--tracks" role="group" aria-label="Make a track from a saved video">
			{VIDEO_INTENTS.filter(({ key }) => videos[key]).map(({ key, label }) => {
				const state = trackStateFor(key);
				if (state === 'unavailable') return null;
				const b = TRACK_BUTTON[state] || TRACK_BUTTON.idle;
				const blocked = disabled || state === 'working' || state === 'done';
				return (
					<button
						key={key}
						type="button"
						className={`ld-vslots__track is-${state}`}
						disabled={blocked}
						onClick={() => {
							if (blocked) return;
							onMakeTrack(key);
						}}>
						<i className={`fa-sharp fa-solid ${b.icon}`} aria-hidden="true" />
						{b.text(label)}
					</button>
				);
			})}
		</div>
	)}
	</>
);

export default VideoSlots;
